import type { GHGScope } from '../types';

export interface ClimatiqActivityPreset {
  id: string;
  label: string;
  scope: GHGScope;
  activityCategory: string;
  climatiqActivityId: string;
  unitType?: string;
  defaultUnit: string;
  allowedUnits?: string[];
  notes?: string;
}

const PRESETS: ClimatiqActivityPreset[] = [
  {
    id: 'scope1_stationary_natural_gas',
    label: 'Stationary Natural Gas Combustion',
    scope: 1,
    activityCategory: 'stationary_combustion',
    climatiqActivityId: 'fuel_combustion-fuel_source_natural_gas',
    defaultUnit: 'kWh',
    allowedUnits: ['kWh', 'MJ', 'therm', 'MMBTU'],
    notes: 'Boilers, furnaces, and other fixed equipment consuming natural gas.',
  },
  {
    id: 'scope1_mobile_fleet_diesel_distance',
    label: 'Fleet Cars - Diesel (Distance)',
    scope: 1,
    activityCategory: 'mobile_combustion',
    climatiqActivityId:
      'passenger_vehicle-vehicle_type_car-fuel_source_diesel-engine_size_na-vehicle_age_na-vehicle_weight_na',
    defaultUnit: 'km',
    allowedUnits: ['km', 'mi'],
    notes: 'Distance-based fleet emissions for diesel passenger cars.',
  },
  {
    id: 'scope1_mobile_fleet_petrol_distance',
    label: 'Fleet Cars - Petrol (Distance)',
    scope: 1,
    activityCategory: 'mobile_combustion',
    climatiqActivityId:
      'passenger_vehicle-vehicle_type_car-fuel_source_petrol-engine_size_na-vehicle_age_na-vehicle_weight_na',
    defaultUnit: 'km',
    allowedUnits: ['km', 'mi'],
    notes: 'Distance-based fleet emissions for petrol passenger cars.',
  },
  {
    id: 'scope1_mobile_fleet_bev_distance',
    label: 'Fleet Cars - Battery Electric (Distance)',
    scope: 1,
    activityCategory: 'mobile_combustion',
    climatiqActivityId:
      'passenger_vehicle-vehicle_type_car-fuel_source_bev-engine_size_na-vehicle_age_na-vehicle_weight_na',
    defaultUnit: 'km',
    allowedUnits: ['km', 'mi'],
    notes: 'Distance-based emissions for electric fleet cars.',
  },
  {
    id: 'scope1_process_cement',
    label: 'Process Emissions - Cement',
    scope: 1,
    activityCategory: 'process_emissions',
    climatiqActivityId: 'cement_production-cement_type_portland',
    defaultUnit: 't',
    allowedUnits: ['kg', 't', 'ton'],
  },
  {
    id: 'scope1_fugitive_refrigerant_r134a',
    label: 'Fugitive Refrigerant Leakage (R-134a)',
    scope: 1,
    activityCategory: 'fugitive_emissions',
    climatiqActivityId: 'refrigerant_leakage-refrigerant_type_r134a',
    defaultUnit: 'kg',
    allowedUnits: ['kg', 'lb', 't'],
  },
  {
    id: 'scope2_purchased_electricity_grid',
    label: 'Purchased Electricity - Grid Mix',
    scope: 2,
    activityCategory: 'purchased_electricity',
    climatiqActivityId: 'electricity-energy_source_grid_mix',
    defaultUnit: 'kWh',
    allowedUnits: ['kWh', 'MWh', 'MJ'],
  },
  {
    id: 'scope2_purchased_steam',
    label: 'Purchased Steam',
    scope: 2,
    activityCategory: 'purchased_steam',
    climatiqActivityId: 'steam-generation_source_na',
    defaultUnit: 'kWh',
    allowedUnits: ['kWh', 'MWh', 'MJ'],
  },
  {
    id: 'scope2_purchased_heating',
    label: 'Purchased District Heating',
    scope: 2,
    activityCategory: 'purchased_heating',
    climatiqActivityId: 'heat-energy_source_district_heating',
    defaultUnit: 'kWh',
    allowedUnits: ['kWh', 'MWh', 'MJ'],
  },
  {
    id: 'scope2_purchased_cooling',
    label: 'Purchased District Cooling',
    scope: 2,
    activityCategory: 'purchased_cooling',
    climatiqActivityId: 'cooling-energy_source_district_cooling',
    defaultUnit: 'kWh',
    allowedUnits: ['kWh', 'MWh', 'MJ'],
  },
  {
    id: 'scope3_purchased_goods_general',
    label: 'Purchased Goods & Services - General',
    scope: 3,
    activityCategory: 'purchased_goods_services',
    climatiqActivityId: 'procurement_spend-type_general_goods',
    defaultUnit: 'kg',
  },
  {
    id: 'scope3_capital_goods_general',
    label: 'Capital Goods - General',
    scope: 3,
    activityCategory: 'capital_goods',
    climatiqActivityId: 'procurement_spend-type_capital_goods',
    defaultUnit: 'kg',
  },
  {
    id: 'scope3_fuel_energy_activities',
    label: 'Fuel & Energy Activities - Upstream Fuels',
    scope: 3,
    activityCategory: 'fuel_energy_activities',
    climatiqActivityId: 'fuel-type_natural_gas-fuel_use_na',
    defaultUnit: 'kWh',
    allowedUnits: ['kWh', 'MWh', 'MJ', 'GJ'],
  },
  {
    id: 'scope3_fuel_energy_activities_electricity',
    label: 'Fuel & Energy Activities - Purchased Electricity Losses',
    scope: 3,
    activityCategory: 'fuel_energy_activities',
    climatiqActivityId: 'electricity-supply_grid-source_supplier_mix_losses',
    defaultUnit: 'kWh',
    allowedUnits: ['kWh', 'MWh', 'MJ', 'GJ'],
  },
  {
    id: 'scope3_upstream_transportation_road',
    label: 'Upstream Transportation - Road Freight',
    scope: 3,
    activityCategory: 'upstream_transportation',
    climatiqActivityId: 'freight_vehicle-vehicle_type_truck',
    defaultUnit: 'tkm',
  },
  {
    id: 'scope3_waste_operations_landfill',
    label: 'Waste in Operations - Landfill',
    scope: 3,
    activityCategory: 'waste_operations',
    climatiqActivityId: 'waste_disposal-method_landfill',
    defaultUnit: 'kg',
  },
  {
    id: 'scope3_business_travel_air',
    label: 'Business Travel - Air',
    scope: 3,
    activityCategory: 'business_travel',
    climatiqActivityId: 'passenger_flight-route_type_domestic-aircraft_type_average',
    defaultUnit: 'pkm',
  },
  {
    id: 'scope3_employee_commuting_car',
    label: 'Employee Commuting - Car',
    scope: 3,
    activityCategory: 'employee_commuting',
    climatiqActivityId:
      'passenger_vehicle-vehicle_type_car-fuel_source_na-engine_size_na-vehicle_age_na-vehicle_weight_na',
    defaultUnit: 'km',
  },
  {
    id: 'scope3_upstream_leased_assets_building',
    label: 'Upstream Leased Assets - Building Energy',
    scope: 3,
    activityCategory: 'upstream_leased_assets',
    climatiqActivityId: 'electricity-energy_source_grid_mix',
    defaultUnit: 'kWh',
  },
  {
    id: 'scope3_upstream_leased_building_heating',
    label: 'Upstream Leased Assets - Building Heating',
    scope: 3,
    activityCategory: 'upstream_leased_assets',
    climatiqActivityId: 'fuel_combustion-fuel_source_natural_gas',
    defaultUnit: 'kWh',
  },
  {
    id: 'scope3_upstream_leased_vehicle',
    label: 'Upstream Leased Assets - Vehicle Fuel',
    scope: 3,
    activityCategory: 'upstream_leased_assets',
    climatiqActivityId: 'fuel_combustion-fuel_source_diesel',
    defaultUnit: 'l',
  },
  {
    id: 'scope3_downstream_transportation_road',
    label: 'Downstream Transportation - Road Freight',
    scope: 3,
    activityCategory: 'downstream_transportation',
    climatiqActivityId: 'freight_vehicle-vehicle_type_truck',
    defaultUnit: 'tkm',
  },
  {
    id: 'scope3_processing_sold_products',
    label: 'Processing of Sold Products - General',
    scope: 3,
    activityCategory: 'processing_sold_products',
    climatiqActivityId: 'industrial_process-general',
    defaultUnit: 'kg',
  },
  {
    id: 'scope3_use_sold_products',
    label: 'Use of Sold Products - Electricity Use',
    scope: 3,
    activityCategory: 'use_sold_products',
    climatiqActivityId: 'electricity-energy_source_grid_mix',
    defaultUnit: 'kWh',
  },
  {
    id: 'scope3_end_of_life_sold_products',
    label: 'End-of-Life of Sold Products - Landfill',
    scope: 3,
    activityCategory: 'end_of_life_sold_products',
    climatiqActivityId: 'waste_disposal-method_landfill',
    defaultUnit: 'kg',
  },
  {
    id: 'scope3_downstream_leased_assets',
    label: 'Downstream Leased Assets - Building Energy',
    scope: 3,
    activityCategory: 'downstream_leased_assets',
    climatiqActivityId: 'electricity-energy_source_grid_mix',
    defaultUnit: 'kWh',
  },
  {
    id: 'scope3_downstream_leased_building_heating',
    label: 'Downstream Leased Assets - Building Heating',
    scope: 3,
    activityCategory: 'downstream_leased_assets',
    climatiqActivityId: 'fuel_combustion-fuel_source_natural_gas',
    defaultUnit: 'kWh',
  },
  {
    id: 'scope3_downstream_leased_vehicle',
    label: 'Downstream Leased Assets - Vehicle Fuel',
    scope: 3,
    activityCategory: 'downstream_leased_assets',
    climatiqActivityId: 'fuel_combustion-fuel_source_diesel',
    defaultUnit: 'l',
  },
  {
    id: 'scope3_franchises',
    label: 'Franchises - Electricity Use',
    scope: 3,
    activityCategory: 'franchises',
    climatiqActivityId: 'electricity-energy_source_grid_mix',
    defaultUnit: 'kWh',
  },
  {
    id: 'scope3_investments',
    label: 'Investments - Portfolio Activity',
    scope: 3,
    activityCategory: 'investments',
    climatiqActivityId: 'financial_portfolio-average',
    defaultUnit: 'kg',
  },
];

function normalizeCategory(activityCategory: string): string {
  return activityCategory.trim().toLowerCase();
}

export function inferUnitTypeFromDefaultUnit(defaultUnit: string): string {
  const normalized = defaultUnit.trim().toLowerCase();
  if (normalized === 'tkm') return 'WeightOverDistance';
  if (normalized === 'pkm') return 'PassengerOverDistance';
  if (['kwh', 'mwh', 'mj', 'gj', 'tj', 'btu', 'therm', 'mmbtu'].includes(normalized)) {
    return 'Energy';
  }
  if (['km', 'mi', 'm', 'nmi', 'ft'].includes(normalized)) return 'Distance';
  if (['kg', 'g', 't', 'ton', 'lb'].includes(normalized)) return 'Weight';
  if (['l', 'ml', 'm3', 'gallon_us', 'bbl', 'cubic_foot'].includes(normalized)) return 'Volume';
  return 'Number';
}

export function getPresetUnitType(preset: ClimatiqActivityPreset): string {
  return preset.unitType ?? inferUnitTypeFromDefaultUnit(preset.defaultUnit);
}

export function getActivitiesFor(
  scope: GHGScope,
  activityCategory: string
): ClimatiqActivityPreset[] {
  const normalized = normalizeCategory(activityCategory);
  return PRESETS.filter(
    (preset) => preset.scope === scope && normalizeCategory(preset.activityCategory) === normalized
  );
}

export function getActivityById(id: string): ClimatiqActivityPreset | undefined {
  return PRESETS.find((preset) => preset.id === id);
}

export function toActivityTypeValue(activityPresetId: string): string {
  return activityPresetId.trim().slice(0, 100);
}
